import {
	ACS_DATA_NAME_ACCESSOR,
	GEOGRAPHY_OPTIONS,
	LOOKUP_TABLE_FIELDS,
	RURAL_DEFINITION_LAYER_ID_PREFIX
} from './constants'
import {json2csv} from "json-2-csv";
import {saveAs} from "file-saver";
import {extent as d3Extent} from "d3-array";
import {stateNameAbbreviatedSelector} from "./Redux/selectors";
import {TableCell} from "./UIComponents/utilsJSX";
import {humanReadableValue, smartDisplayValue} from "./UIComponents/utilsDisplay";

const STATE_FIP_CODE_LENGTH = 2
const COUNTY_FIP_CODE_LENGTH = 3
const TRACT_FIP_CODE_LENGTH = 6

export const getCountyFIPSCodeFromCensusBlockFIPSCode = censusBlockFIPSCode => String(censusBlockFIPSCode).slice(0, STATE_FIP_CODE_LENGTH + COUNTY_FIP_CODE_LENGTH)
export const getTractFIPSCodeFromCensusBlockFIPSCode = censusBlockFIPSCode => String(censusBlockFIPSCode).slice(0, STATE_FIP_CODE_LENGTH + COUNTY_FIP_CODE_LENGTH + TRACT_FIP_CODE_LENGTH)

export const getGeoIdForSelectedRegion = (correspondingGeoIdsForNonFIPSGeos, selectedGeography) => {
	if (!correspondingGeoIdsForNonFIPSGeos) return undefined
	const censusBlockFIPSCode = correspondingGeoIdsForNonFIPSGeos[LOOKUP_TABLE_FIELDS.censusBlockFIPSCode]
	
	switch (selectedGeography.cartoLayerId) {
		case GEOGRAPHY_OPTIONS.tract.cartoLayerId:
			return getTractFIPSCodeFromCensusBlockFIPSCode(censusBlockFIPSCode)
		case GEOGRAPHY_OPTIONS.county.cartoLayerId:
			return getCountyFIPSCodeFromCensusBlockFIPSCode(censusBlockFIPSCode)
		case GEOGRAPHY_OPTIONS.place.cartoLayerId:
			return correspondingGeoIdsForNonFIPSGeos[LOOKUP_TABLE_FIELDS.placeCode]
		case GEOGRAPHY_OPTIONS.countySubdivision.cartoLayerId:
			return correspondingGeoIdsForNonFIPSGeos[LOOKUP_TABLE_FIELDS.countySubdivisionCode]
		default:
			console.error(`This geography is not supported, try county or tract. censusBlockFIPSCode: ${censusBlockFIPSCode}, selectedGeography: ${selectedGeography}`)
			return censusBlockFIPSCode
	}
}

export const isACensusTract = (cartoLayerId) => cartoLayerId === GEOGRAPHY_OPTIONS.tract.cartoLayerId
export const isACensusPlace = (cartoLayerId) => cartoLayerId === GEOGRAPHY_OPTIONS.place.cartoLayerId
export const getLayerSpecificFields = (cartoLayerId) => {
	return isACensusTract(cartoLayerId) ? [] : ['wikipedia']
}

export const fieldValueIsALink = fieldValue => (typeof fieldValue === 'string') && (fieldValue.toLowerCase().startsWith('http') || fieldValue.toLowerCase().startsWith('www.'))

export const isSelectedOrComparisonRegion = (geoid, selectedRegionInfo = {}, comparisonRegionInfo = {}) => {
	const selected = geoid === selectedRegionInfo.geoIdForSelectedRegion
	const comparison = geoid === comparisonRegionInfo.geoIdForSelectedRegion
	return {
		selected,
		comparison,
		either: selected || comparison,
	}
}

export const processCartoCategoricalData = cartoACSData => {
	const fieldValues = {}

	// cartoACSData.forEach(d => {
	// 	const categorical = Object.keys(d).filter(k => k in ACS_CATEGORICAL_FIELDS)
	// 	categorical.forEach(c => {
	// 		if (!(c in fieldValues))
	// 			fieldValues[c] = {}
	// 		const value = d[c]
	// 		if (!(value in fieldValues[c]))
	// 			fieldValues[c][value] = 0
	// 		fieldValues[c][value] += 1
	// 	})
	// })

	return {categoricalFields: fieldValues}
}

export const getVisiblePointsHash = (filteredData) => {
	const visiblePointsHash = new Set()
	filteredData
		.forEach(p => {
			visiblePointsHash.add(p[ACS_DATA_NAME_ACCESSOR]) // technically ACS_DATA_NAME_ACCESSOR is what sets "connectedLineGroup", which we use for filtering
		})

	return visiblePointsHash
}

export const fieldIsWithinBounds = (region, fieldFilterBounds, filteredFieldName) => {
	const [filterLowerBound, filterUpperBound] = fieldFilterBounds[filteredFieldName].filterBounds
	const regionFieldValue = region[filteredFieldName]
	return (regionFieldValue >= filterLowerBound) && (regionFieldValue <= filterUpperBound) // inclusive filter
}

export const numSort = (a, b, accessor) => {
	return (a[accessor] || 0) - (b[accessor] || 0); // Handle undefined as zero
};

export const strSort = (a, b, accessor) => {
	const valueA = a[accessor]
	const valueB = b[accessor]
	if (typeof(valueA) !== "string" || typeof(valueB) !== "string") return -1 // Handle undefined/"" as lowest alphabetical order
	
	const valueALowerCase = valueA.toLowerCase()
	const valueBLowerCase = valueB.toLowerCase()
	if (valueALowerCase < valueBLowerCase) return -1 //sort string ascending
	if (valueALowerCase > valueBLowerCase) return 1
	return 0 //default return value (no sorting)
}

export const dataPointIsFilteredOut = ({filterBounds}, {rangeAccessor}) => filterBounds && (filterBounds[0] > rangeAccessor || filterBounds[1] < rangeAccessor)

export const downloadAsCSV = (data, fileName = "rural_opportunity_map_filtered_communities") => {
	json2csv(data, (err, csv) => {
		if (!err) {
			const blob = new Blob([csv], {type: "text/plain;charset=utf-8"})
			saveAs(blob, `${fileName}.csv`)
		}
	})
}

const TOLERANCE = 0.00001 // because of the way we map between the actual value and a scaled version, rounding errors are expected
export const boundsHaveChangedSignificantly = (existingBounds1, newBounds, tolerance = TOLERANCE) => {
	const [currLow, currHigh] = existingBounds1 || []
	const [newLow, newHigh] = newBounds || []
	return isSignificantlyDifferent(newLow, currLow, tolerance) || isSignificantlyDifferent(newHigh, currHigh, tolerance)
}

export const isSignificantlyDifferent = (value1, value2, tolerance = TOLERANCE) => Math.abs(value1 - value2) > tolerance

export const actuallyRecalculateFieldExtents = (ACSDataForStates) => ACSDataForStates.reduce((_sortedByField, row, i) => {
	//console.log('CALCULATING fieldExtents...')
	Object.keys(row).forEach((fieldName) => {
		_sortedByField[fieldName] = [...(_sortedByField[fieldName] || []), row[fieldName]]
	})
	if (i === (ACSDataForStates.length - 1)) {
		//console.log('...DONE')
		return Object.keys(_sortedByField).reduce((fieldExtents, fieldName) => {
			fieldExtents[fieldName] = d3Extent(_sortedByField[fieldName])
			return fieldExtents
		}, {})
	}
	return _sortedByField
}, {})

export const groupArrayIntoChunks = (inputArray, maxChunkSize = 10) => {
	return inputArray.reduce((all, one, i) => {
		const ch = Math.floor(i / maxChunkSize);
		all[ch] = [].concat((all[ch] || []), one);
		return all
	}, [])
}

export const arraysItemsAreShallowlyEqual = (array1, array2) => {
	const atLeastOneIsNotAnArray = !array1 || !array2
	const areDifferent = atLeastOneIsNotAnArray
		|| array1.length !== array2.length
		|| array1.some((item, i) => array2[i] !== array1[i])
	return !areDifferent
}

export const SortableResultsTableCell = (text, row, fieldName, isRegionNameColumn, isTractNameColumn) => {
	if (isRegionNameColumn) {
		const stateNameAbbreviated = stateNameAbbreviatedSelector(row)
		const regionAndState = `${text}, ${stateNameAbbreviated}`
		return isTractNameColumn ? TableCell(`OZ in ${regionAndState}`) : TableCell(regionAndState)
	}
	return TableCell(humanReadableValue(fieldName, smartDisplayValue(text)))
}

export const isAttrLayer = layerId => layerId && layerId.startsWith('attr_')

export const isARuralDefinition = layerId => layerId.includes(RURAL_DEFINITION_LAYER_ID_PREFIX)
